import _ from 'lodash';
import {
    getPaymentItemCptDosKey,
    getPaymentItemCptKey,
    getProfessionalServiceLineCptDosKey,
    getProfessionalServiceLineCptKey,
} from './matching-keys';

export interface ProfessionalMatchingPaymentItem {
    controlNumber?: string;
    procedureCode?: string;
    modifierCodes?: string[];
    fromDos?: string;
    paymentId?: string;
}

export interface ProfessionalMatchingServiceLine {
    x12ControlNumber?: string;
    f24dProcedureCode?: string;
    f24dProcedureModifiers?: string[];
    f24aFromDateOfService?: string;
}

export function createProfessionalPaymentItemMatcher<TItem extends ProfessionalMatchingPaymentItem>(
    paymentItems: TItem[]
) {
    const itemsByCptDosKey = new Map<string, TItem[]>();
    const itemsByCptKey = new Map<string, TItem[]>();
    const usedPaymentItems = new Set<TItem>();

    paymentItems.forEach(item => {
        const cptKey = getPaymentItemCptKey(item);
        if (cptKey) {
            const items = itemsByCptKey.get(cptKey) || [];
            items.push(item);
            itemsByCptKey.set(cptKey, items);
        }

        const cptDosKey = getPaymentItemCptDosKey(item);
        if (cptDosKey) {
            const items = itemsByCptDosKey.get(cptDosKey) || [];
            items.push(item);
            itemsByCptDosKey.set(cptDosKey, items);
        }
    });

    const pickUnusedOnePerPaymentId = (candidates: TItem[]): TItem[] => {
        const chosen: TItem[] = [];
        const usedPaymentIds = new Set<string>();

        for (const item of candidates) {
            if (usedPaymentItems.has(item)) {
                continue;
            }

            const paymentId = item.paymentId;
            if (paymentId) {
                if (usedPaymentIds.has(paymentId)) {
                    continue;
                }

                usedPaymentIds.add(paymentId);
            }

            chosen.push(item);
        }

        return chosen;
    };

    const take = (candidates: TItem[]): TItem[] => {
        const foundPaymentItems = pickUnusedOnePerPaymentId(candidates);
        foundPaymentItems.forEach(item => usedPaymentItems.add(item));
        return foundPaymentItems;
    };

    /** Pass 1 evidence: same CPT and same date of service, modifiers ignored. */
    const cptDosCandidates = (line: ProfessionalMatchingServiceLine): TItem[] => {
        const cptDosKey = getProfessionalServiceLineCptDosKey(line);
        return cptDosKey ? itemsByCptDosKey.get(cptDosKey) || [] : [];
    };

    // CPT only when DOS is missing on the line, the item, or both. Two dates that disagree
    // still do not match — that would pay the wrong day's line.
    const cptOnlyCandidates = (line: ProfessionalMatchingServiceLine): TItem[] => {
        const cptKey = getProfessionalServiceLineCptKey(line);
        if (!cptKey) {
            return [];
        }

        const lineDos = line.f24aFromDateOfService ? line.f24aFromDateOfService.trim() : '';
        return (itemsByCptKey.get(cptKey) || []).filter(item => {
            const itemDos = item.fromDos ? item.fromDos.trim() : '';
            return !lineDos || !itemDos;
        });
    };

    const normalizeControlNumber = (value?: string): string => _.toUpper(_.trim(value || ''));
    const modifierSignature = (modifiers?: string[]): string =>
        _.sortBy(_.compact(_.map(modifiers, modifier => _.toUpper(_.trim(modifier))))).join(',');

    type Preference = (line: ProfessionalMatchingServiceLine, candidates: TItem[]) => TItem[];

    const sameControlNumber: Preference = (line, candidates) => {
        const lineControlNumber = normalizeControlNumber(line.x12ControlNumber);
        return lineControlNumber
            ? candidates.filter(item => normalizeControlNumber(item.controlNumber) === lineControlNumber)
            : [];
    };

    const sameModifiers: Preference = (line, candidates) => {
        const lineModifiers = modifierSignature(line.f24dProcedureModifiers);
        return candidates.filter(item => modifierSignature(item.modifierCodes) === lineModifiers);
    };

    const anyCandidate: Preference = (_line, candidates) => candidates;

    /**
     * Tie-breaks, strongest first. They only ever choose AMONG candidates the pass already allowed,
     * so a control number can never make two different CPTs match — CPT + DOS decides whether a
     * line and a payment can match; these decide which line gets which payment.
     */
    const TIE_BREAKS: Preference[] = [sameControlNumber, sameModifiers, anyCandidate];

    /**
     * One matching pass over every still-unmatched line.
     *
     * A line alone on its key has nothing to be confused with: it takes every unused candidate, one
     * per payment, exactly as before. Only when two or more lines share a key do the tie-breaks run,
     * each for every contending line before the next — otherwise `99213-25` would take the payment
     * for `99213-59` just by being listed first, and a wasted-drug `J1885-JW` line would take the
     * payment for the administered `J1885`.
     *
     * Tie-breaks run inside one ERA at a time. A later $0 duplicate that echoes the 837 LX
     * control number must not consume the line and leave the paying 835's SVC unmatched —
     * 835s often put sequence numbers ("1", "3") in SVC controlNumber, not the LX value.
     */
    const runPass = (
        lines: ProfessionalMatchingServiceLine[],
        matched: TItem[][],
        keyOf: (line: ProfessionalMatchingServiceLine) => string | undefined,
        candidatesFor: (line: ProfessionalMatchingServiceLine) => TItem[],
    ): void => {
        const locked = matched.map(items => items.length > 0);
        const contenders = new Map<string, number>();
        lines.forEach((line, index) => {
            if (locked[index]) {
                return;
            }
            const key = keyOf(line);
            if (key) {
                contenders.set(key, (contenders.get(key) ?? 0) + 1);
            }
        });

        const paymentIds: Array<string | undefined> = [];
        const seenPaymentIds = new Set<string | undefined>();
        paymentItems.forEach(item => {
            if (!seenPaymentIds.has(item.paymentId)) {
                seenPaymentIds.add(item.paymentId);
                paymentIds.push(item.paymentId);
            }
        });

        paymentIds.forEach(paymentId => {
            TIE_BREAKS.forEach((prefer, step) => {
                const isLastStep = step === TIE_BREAKS.length - 1;

                lines.forEach((line, index) => {
                    if (locked[index]) {
                        return;
                    }
                    if (paymentId && matched[index].some(item => item.paymentId === paymentId)) {
                        return;
                    }
                    const key = keyOf(line);
                    if (!key) {
                        return;
                    }
                    const contested = (contenders.get(key) ?? 0) > 1;
                    if (!contested && !isLastStep) {
                        return;
                    }

                    const paymentCandidates = candidatesFor(line).filter(item => item.paymentId === paymentId);
                    const found = take(prefer(line, paymentCandidates));
                    if (found.length) {
                        matched[index] = [...matched[index], ...found];
                    }
                });
            });
        });
    };

    /**
     * CPT + DOS for every line first, then CPT-only for lines still unmatched. Running CPT-only
     * line by line would let a no-DOS line take an item a later line matches on date.
     */
    const matchLines = (lines: ProfessionalMatchingServiceLine[]): TItem[][] => {
        const matched: TItem[][] = lines.map(() => []);

        runPass(lines, matched, getProfessionalServiceLineCptDosKey, cptDosCandidates);
        runPass(lines, matched, getProfessionalServiceLineCptKey, cptOnlyCandidates);

        return matched;
    };

    return {
        matchLines,
        getUsedPaymentItems: () => usedPaymentItems,
    };
}
